import { type ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { resolveProjectDisplayStatus } from '@/features/shared/utils/projectStatusPresentation';
import { isLightTheme } from '@/theme/semanticTokens';
import { r, s } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { Stack } from '@/ui/layout/Stack';
import {
  Box,
  darken,
  ErrorOutline,
  formatStatusLabel,
  Link,
  Text,
  Tooltip,
  useTheme,
} from '@/ui/primitives';
import { resolveChartPlotAreaBackgroundColor } from '@/ui/primitives/chartPlotAreaBackgroundColor';
import {
  BlockOutlinedIcon,
  CancelOutlinedIcon,
  CheckCircleOutlineIcon,
  HelpIcon,
  PlayCircleFilledWhiteOutlinedIcon,
  ScheduleIcon as ScheduleOutlinedIcon,
} from '@/ui/primitives/icons';
import { overlayScrollbarSx } from '@/ui/primitives/overlayScrollbarSx';
import type { DailyHistoryCell, DailyStatus } from './EntityDailyHistoryPanel.types';
import type { SvgIconComponent } from '@/ui/primitives/types';

const HISTORY_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      maxWidth: 'none',
      width: 'fit-content',
      fontSize: '15px',
      lineHeight: 1.4,
      p: 0,
    },
  },
} as const;
const HISTORY_TOOLTIP_ENTER_DELAY_MS = 250;
const HISTORY_TOOLTIP_TIME_COLUMN_WIDTH = '7ch';
const HISTORY_TOOLTIP_TIME_TO_STATUS_GAP = 'sm';
const HISTORY_TOOLTIP_STATUS_TO_RUN_GAP = 'none';
const HISTORY_TOOLTIP_MAX_HEIGHT = '480px';

const getTooltipStatusIcon = (
  status: Exclude<DailyStatus, null>,
  groupErroredWithFailed: boolean,
): SvgIconComponent => {
  const displayStatus = resolveProjectDisplayStatus(status, groupErroredWithFailed);

  if (displayStatus === 'passed' || displayStatus === 'completed' || displayStatus === 'success') {
    return CheckCircleOutlineIcon;
  }
  if (displayStatus === 'failed' || displayStatus === 'canceled' || displayStatus === 'timed_out') {
    return CancelOutlinedIcon;
  }
  if (displayStatus === 'errored') return ErrorOutline;
  if (displayStatus === 'running') return PlayCircleFilledWhiteOutlinedIcon;
  if (displayStatus === 'skipped') return BlockOutlinedIcon;
  if (
    displayStatus === 'action_required' ||
    displayStatus === 'warning' ||
    displayStatus === 'neutral'
  ) {
    return ErrorOutline;
  }
  if (displayStatus === 'queued' || displayStatus === 'waiting') {
    return ScheduleOutlinedIcon;
  }
  if (displayStatus === 'unknown') {
    return HelpIcon;
  }
  return HelpIcon;
};

const resolveDailyStatusBaseColor = (
  status: DailyStatus,
  groupErroredWithFailed: boolean,
): string => {
  const displayStatus = resolveProjectDisplayStatus(status, groupErroredWithFailed);

  switch (displayStatus) {
    case null:
      return 'transparent';
    case 'passed':
    case 'completed':
    case 'success':
      return uiTokens.statusColors.green;
    case 'failed':
    case 'canceled':
    case 'timed_out':
      return uiTokens.statusColors.red;
    case 'errored':
      return uiTokens.statusColors.brown;
    case 'warning':
    case 'action_required':
    case 'skipped':
      return uiTokens.statusColors.orange;
    case 'neutral':
    case 'unknown':
    case 'untested':
      return uiTokens.statusColors.neutral;
    case 'running':
      return uiTokens.statusColors.blue;
    case 'queued':
    case 'waiting':
      return uiTokens.statusColors.neutral;
    default:
      return 'transparent';
  }
};

const resolveDailyStatusBorderColor = (status: DailyStatus): string =>
  status === null ? 'divider' : 'transparent';

const resolveDailyStatusColor = (
  status: DailyStatus,
  groupErroredWithFailed: boolean,
  mode: 'light' | 'dark',
): string => {
  const baseColor = resolveDailyStatusBaseColor(status, groupErroredWithFailed);
  if (status === null) return baseColor;
  return mode === 'dark' ? darken(baseColor, 0.26) : baseColor;
};

const formatHistoryTooltipTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(value);

const formatHistoryTooltipDate = (value: Date): string =>
  new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(value);

const renderHistoryTooltip = (
  historyCell: DailyHistoryCell,
  activityLabel: 'executions' | 'builds' | 'jobs' | 'runs',
  linksDisabled: boolean,
  groupErroredWithFailed: boolean,
): ReactElement => (
  <Box sx={{ width: 'fit-content', minWidth: 'fit-content' }}>
    <Stack
      gap="xxs"
      sx={[
        {
          maxHeight: HISTORY_TOOLTIP_MAX_HEIGHT,
          overflowY: 'auto',
          px: s('md'),
          py: s('xs'),
        },
        overlayScrollbarSx,
      ]}
    >
      <Box
        sx={{
          pb: s('xxs'),
          mb: s('xxs'),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Text
          role="body"
          sx={{
            fontSize: (theme) => theme.typography.pxToRem(15),
            whiteSpace: 'nowrap',
          }}
        >
          {formatHistoryTooltipDate(historyCell.day)}
        </Text>
      </Box>
      {historyCell.details.length === 0 ? (
        <Text
          role="body"
          sx={{
            color: 'text.secondary',
            fontSize: (theme) => theme.typography.pxToRem(15),
            whiteSpace: 'nowrap',
          }}
        >
          {activityLabel === 'executions' || activityLabel === 'runs'
            ? 'No runs'
            : activityLabel === 'jobs'
              ? 'No jobs'
              : 'No jobs'}
        </Text>
      ) : (
        historyCell.details.map((detail, detailIndex) => {
          const displayStatus = resolveProjectDisplayStatus(detail.status, groupErroredWithFailed);
          const StatusIcon = getTooltipStatusIcon(detail.status, groupErroredWithFailed);
          return (
            <Box
              key={`${historyCell.day.toISOString()}-${String(detailIndex)}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: `${HISTORY_TOOLTIP_TIME_COLUMN_WIDTH} max-content max-content`,
                alignItems: 'center',
                columnGap: s(HISTORY_TOOLTIP_TIME_TO_STATUS_GAP),
                minWidth: 0,
              }}
            >
              <Text
                role="body"
                sx={{
                  color: 'text.secondary',
                  fontSize: (theme) => theme.typography.pxToRem(15),
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatHistoryTooltipTime(detail.timestamp)}
              </Text>
              <Box
                component={StatusIcon}
                sx={{
                  fontSize: (theme) => theme.app.iconSizes.sm,
                  flexShrink: 0,
                  justifySelf: 'center',
                  color: resolveDailyStatusBaseColor(detail.status, groupErroredWithFailed),
                }}
              />
              <Text
                role="body"
                sx={{
                  fontSize: (theme) => theme.typography.pxToRem(15),
                  whiteSpace: 'nowrap',
                  pl: s(HISTORY_TOOLTIP_STATUS_TO_RUN_GAP),
                  pr: s('sm'),
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {detail.runHref && detail.runLabel && !linksDisabled ? (
                  <Link
                    component={RouterLink}
                    to={detail.runHref}
                    underline="hover"
                    color="inherit"
                    sx={{
                      display: 'inline-block',
                      width: 'fit-content',
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {detail.runLabel}
                  </Link>
                ) : detail.detailHref && !linksDisabled ? (
                  <Link
                    component={RouterLink}
                    to={detail.detailHref}
                    underline="hover"
                    color="inherit"
                    sx={{
                      display: 'inline-block',
                      width: 'fit-content',
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatStatusLabel(displayStatus)}
                  </Link>
                ) : (
                  (detail.runLabel ?? formatStatusLabel(displayStatus))
                )}
              </Text>
            </Box>
          );
        })
      )}
    </Stack>
  </Box>
);

export const HistoryStatusesGrid = ({
  rowId,
  dailyHistory,
  blockSize,
  activityLabel,
  linksDisabled,
}: {
  rowId: string;
  dailyHistory: DailyHistoryCell[];
  blockSize: number;
  activityLabel: 'executions' | 'builds' | 'jobs' | 'runs';
  linksDisabled: boolean;
}): ReactElement => {
  const { groupErroredWithFailed } = useProjectStatusPresentation();
  const theme = useTheme();
  const emptyStatusBlockColor = resolveChartPlotAreaBackgroundColor(theme);
  const colorMode = isLightTheme(theme) ? 'light' : 'dark';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${String(dailyHistory.length)}, minmax(0, 1fr))`,
        columnGap: s('md'),
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
      }}
    >
      {dailyHistory.map((historyCell) => (
        <Tooltip
          key={`${rowId}-${historyCell.day.toISOString()}`}
          title={renderHistoryTooltip(
            historyCell,
            activityLabel,
            linksDisabled,
            groupErroredWithFailed,
          )}
          arrow
          enterDelay={HISTORY_TOOLTIP_ENTER_DELAY_MS}
          enterNextDelay={HISTORY_TOOLTIP_ENTER_DELAY_MS}
          slotProps={HISTORY_TOOLTIP_SLOT_PROPS}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              minWidth: 0,
              width: '100%',
            }}
          >
            <Box
              sx={{
                width: '100%',
                minWidth: `${blockSize}px`,
                height: `${blockSize}px`,
                borderRadius: r('sm'),
                border: '1px solid',
                borderColor: resolveDailyStatusBorderColor(historyCell.status),
                backgroundColor:
                  historyCell.status === null ? emptyStatusBlockColor : 'transparent',
                display: 'flex',
                overflow: 'hidden',
              }}
            >
              {historyCell.statuses.length === 0
                ? null
                : historyCell.statuses.map((status, statusIndex) => (
                    <Box
                      key={`${rowId}-${historyCell.day.toISOString()}-${String(statusIndex)}`}
                      sx={{
                        flex: '1 1 0',
                        height: '100%',
                        backgroundColor: resolveDailyStatusColor(
                          status,
                          groupErroredWithFailed,
                          colorMode,
                        ),
                        borderLeft: statusIndex === 0 ? 'none' : '1px solid',
                        borderColor: statusIndex === 0 ? 'transparent' : 'background.paper',
                      }}
                    />
                  ))}
            </Box>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
};
